<script setup lang="ts">
import { computed, ref } from 'vue'
import { deleteServiceProviderAction } from '@/api/functions/services/providers/delete-service-provider.action.ts'
import { getServiceProvidersAction } from '@/api/functions/services/providers/get-service-providers.action.ts'
import { toggleServiceProviderActivationAction } from '@/api/functions/services/providers/toggle-service-provider-activation.action.ts'
import { useMutationApi, useQueryApi } from '@/composables/useApi.ts'
import { useToast } from '@/composables/useToast'
import { settingsTabs } from '@/constants/setting-tabs.ts'
import { getErrorMessage } from '@/utils/errorUtils.ts'
import SettingsOverview from '../partials/SettingsOverview.vue'
import CreateProviderModal from './partials/CreateProviderModal.vue'
import UpdateProviderModal from './partials/UpdateProviderModal.vue'

const showDeleteModal = ref(false)
const showUpdateModal = ref(false)
const showCreateModal = ref(false)

const toast = useToast()

const selectedProvider = ref<null | { id: string }>(null)
const currentPage = ref(1)
const showToggleModal = ref(false)
const selectedToggleProvider = ref<any>(null)

const { data: serviceProvidersData, refetch: refetchServiceProviders } = useQueryApi({
  queryKey: computed(() => ['service-providers', currentPage.value]),
  queryFn: () =>
    getServiceProvidersAction({
      page: currentPage.value,
    }),
})

const itemsPerPage = computed(() => serviceProvidersData.value?.pagination?.perPage ?? 10)

const totalItems = computed(() => serviceProvidersData.value?.pagination?.total ?? 0)

const providerRows = computed(() => {
  const start = ((serviceProvidersData.value?.pagination?.currentPage ?? 1) - 1) * itemsPerPage.value

  return (serviceProvidersData.value?.data ?? []).map((item, idx) => ({
    ...item,
    index: start + idx + 1,
    statusLabel: item.is_active ? 'فعال' : 'غیرفعال',
    website_url: item.website_url ?? '-',
  }))
})

// Columns
const columns = [
  {
    key: 'index',
    label: '#',
    class: 'col-span-1 font-fa text-sm',
  },
  {
    key: 'name',
    label: 'نام ارائه‌دهنده',
    class: 'col-span-2 text-sm',
  },
  {
    key: 'slug',
    label: 'اسلاگ',
    class: 'col-span-2 text-[13px]',
  },
  {
    key: 'website_url',
    label: 'وب‌سایت',
    class: 'col-span-3 text-[13px]',
  },
  {
    key: 'statusLabel',
    label: 'وضعیت',
    class: 'col-span-1 text-sm',
  },
]

// Actions
const actions = [
  {
    label: (row: any) => (row.is_active ? 'غیرفعال کردن' : 'فعال کردن'),
    handlerName: 'toggleProviderActivation',
  },
  {
    label: 'ویرایش',
    handlerName: 'editProvider',
  },
  {
    label: 'حذف',
    handlerName: 'deleteProvider',
  },
]

// Handlers
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditProvider = (provider: any) => {
  selectedProvider.value = provider
  showUpdateModal.value = true

  console.log('Edit provider:', provider)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onDeleteProvider = (provider: any) => {
  selectedProvider.value = provider
  showDeleteModal.value = true
}

const deleteProviderMutation = useMutationApi({
  mutationFn: (id: string) => deleteServiceProviderAction(id),

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا در حذف ارائه‌دهنده')
  },
})

const toggleProviderActivationMutation = useMutationApi({
  mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
    toggleServiceProviderActivationAction(id, {
      is_active,
    }),

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا در تغییر وضعیت ارائه‌دهنده')
  },
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onToggleProviderActivation = (provider: any) => {
  if (!provider?.id) {
    toast.error('ارائه‌دهنده انتخاب نشده است', 'خطا')
    return
  }

  selectedToggleProvider.value = provider
  showToggleModal.value = true
}

const handleToggleProviderActivation = async () => {
  if (!selectedToggleProvider.value?.id) {
    toast.error('ارائه‌دهنده انتخاب نشده است', 'خطا')
    return
  }

  const provider = selectedToggleProvider.value

  try {
    await toggleProviderActivationMutation.mutateAsync({
      id: provider.id,
      is_active: !provider.is_active,
    })

    toast.success(provider.is_active ? 'ارائه‌دهنده غیرفعال شد' : 'ارائه‌دهنده فعال شد')

    showToggleModal.value = false
    selectedToggleProvider.value = null

    await refetchServiceProviders()
  } catch {}
}

const handleDeleteProvider = async () => {
  if (!selectedProvider.value?.id) {
    toast.error('ارائه‌دهنده انتخاب نشده است', 'خطا')
    return
  }

  try {
    await deleteProviderMutation.mutateAsync(selectedProvider.value.id)

    toast.success('ارائه‌دهنده با موفقیت حذف شد')

    showDeleteModal.value = false
    selectedProvider.value = null

    await refetchServiceProviders()
  } catch {}
}

// Pagination
const goToPage = (page: number) => {
  currentPage.value = page
  refetchServiceProviders()
}

const handleProviderUpdated = async () => {
  showUpdateModal.value = false
  showCreateModal.value = false
  selectedProvider.value = null

  await refetchServiceProviders()
}
</script>

<template>
    <div class="w-full mb-12">

        <ConfirmModal v-model="showToggleModal" :title="selectedToggleProvider?.is_active
            ? 'غیرفعال کردن ارائه‌دهنده'
            : 'فعال کردن ارائه‌دهنده'
            " :message="selectedToggleProvider?.is_active
                ? 'آیا از غیرفعال کردن این ارائه‌دهنده اطمینان دارید؟'
                : 'آیا از فعال کردن این ارائه‌دهنده اطمینان دارید؟'
                " :confirm-text="selectedToggleProvider?.is_active
                    ? 'بله غیرفعال شود'
                    : 'بله فعال شود'
                    " cancel-text="بازگشت" :loading="toggleProviderActivationMutation.isPending.value"
            :disabled="toggleProviderActivationMutation.isPending.value" @confirm="handleToggleProviderActivation"
            @cancel="
                showToggleModal = false;
            selectedToggleProvider = null
                " />

        <!-- Inactive Modal -->
        <ConfirmModal v-model="showDeleteModal" title="حذف کردن ارائه‌دهنده"
            message="آیا از حذف این ارائه‌دهنده اطمینان دارید؟" confirm-text="بله حذف شود" cancel-text="بازگشت"
            :loading="deleteProviderMutation.isPending.value" :disabled="deleteProviderMutation.isPending.value"
            @confirm="handleDeleteProvider" @cancel="showDeleteModal = false" />

        <!-- Update Provider -->
        <UpdateProviderModal v-model="showUpdateModal" :selectedProvider="selectedProvider as unknown as Record<string, string>
            " @confirm="handleProviderUpdated" @cancel="showUpdateModal = false" />

        <!-- Create Provider -->
        <CreateProviderModal v-model="showCreateModal" @confirm="handleProviderUpdated"
            @cancel="showCreateModal = false" />

        <SettingsOverview :tabs="settingsTabs">
            <template #actions>
                <div class="flex items-center gap-2 pb-1">
                    <Button :isAdmin="true" @click="showCreateModal = true"
                        class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
                        افزودن ارائه‌دهنده جدید
                    </Button>
                </div>
            </template>
        </SettingsOverview>

        <div class="mt-20 lg:mt-7">
            <Table :columns="columns" :rows="providerRows" :actions="actions"
                @primaryAction="onToggleProviderActivation" @secondaryAction="onEditProvider"
                @thirdAction="onDeleteProvider" />

            <Pagination v-if="totalItems > 0" v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage"
                :totalItems="totalItems" :isAdmin="true" @update:currentPage="goToPage" />
        </div>
    </div>
</template>